<template>
  <form
    ref="form"
    class="form"
    @submit.prevent="save"
  >
    <h2 class="title title">
      {{ guest === mainGuest ? $t('mainGuest') : $t('asideGuest') }}
    </h2>
    <div class="form__title">
      <h3 class="title title--small">
        {{ $t('guest') }}
      </h3>
      <span> {{ guest === mainGuest ? $t('main') : $t('companion') }}</span>
    </div>
    <template
      v-if="isFirstView || (!hasIntegration && activeGuest === mainGuest)"
    >
      <div class="form__row">
        <div
          class="form__row__column"
          :class="{
            error: !isValid('reservationCode', true),
            success: isValid('reservationCode'),
          }"
        >
          <label for="reservationCode">
            {{ $t('reservationCode') }}
          </label>
          <input
            id="reservationCode"
            ref="reservationCode"
            v-model.trim="guest.reservationCode"
            type="text"
            placeholder="000000000"
            maxlength="20"
            @blur="startValidate('reservationCode')"
          />
        </div>
        <div
          class="form__row__column"
          :class="{
            error: !isValid('checkInDate', true),
            success: isValid('checkInDate'),
          }"
        >
          <label for="checkInDate">
            {{ $t('checkInDate') }}
          </label>
          <input
            id="checkInDate"
            ref="checkInDate"
            v-model.trim="guest.checkInDate"
            v-imask="dateMask"
            type="text"
            placeholder="dd/mm/aaaa"
            autocomplete="off"
            @blur="startValidate('checkInDate')"
          />
        </div>
      </div>
    </template>
    <div class="form__row">
      <div
        class="form__row__column"
        :class="{
          error: !isValid('name', true),
          success: isValid('name'),
        }"
      >
        <label for="name">
          {{ $t('name') }}
        </label>
        <input
          id="name"
          v-model.trim="guest.name"
          type="text"
          placeholder="Ex. Maria"
          maxlength="60"
          @blur="startValidate('name')"
        />
      </div>
      <div
        class="form__row__column"
        :class="{
          error: !isValid('surname', true),
          success: isValid('surname'),
        }"
      >
        <label for="surname">
          {{ $t('surname') }}
        </label>
        <input
          id="surname"
          v-model.trim="guest.surname"
          type="text"
          placeholder="Ex. Silva"
          maxlength="60"
          @blur="startValidate('surname')"
        />
      </div>
    </div>
    <template v-if="!hasIntegration || !isFirstView">
      <h3 class="title title--smaller">
        {{ $t('profile') }}
      </h3>
      <div class="form__row">
        <div
          class="form__row__column"
          :class="{
            error: !isValid('birthDate', true),
            success: isValid('birthDate'),
          }"
        >
          <label for="birthDate">
            {{ $t('birthDate') }}
          </label>
          <input
            id="birthDate"
            ref="birthDate"
            v-model.trim="guest.birthDate"
            v-imask="dateMask"
            type="text"
            placeholder="dd/mm/aaaa"
            autocomplete="off"
            @blur="startValidate('birthDate')"
          />
        </div>
      </div>
      <div class="form__row">
        <div
          class="form__row__column"
          :class="{
            error: !isValid('profession', true),
            success: isValid('profession'),
          }"
        >
          <label for="profession">
            {{ $t('profession') }}
            <template v-if="isChild">
              {{ $t('optionalField') }}
            </template>
          </label>
          <select
            v-if="hasPmsIntegration"
            id="profession"
            v-model="guest.profession"
            @change="startValidate('profession')"
          >
            <option value="">
              {{ $t('select') }}
            </option>
            <option
              v-for="profession in professions"
              :key="profession.pt"
              :value="profession.pt"
            >
              {{ profession[language] }}
            </option>
          </select>
          <input
            v-else
            id="profession"
            v-model.trim="guest.profession"
            type="text"
            placeholder="Ex. professor"
            maxlength="60"
            @blur="startValidate('profession')"
          />
        </div>
      </div>
      <div class="form__row">
        <div
          class="form__row__column"
          :class="{
            error: !isValid('nationality', true),
            success: isValid('nationality'),
          }"
        >
          <label for="nationality">
            {{ $t('nationality') }}
          </label>
          <select
            id="nationality"
            v-model="guest.nationality"
            @change="startValidate('nationality')"
          >
            <option value="">
              {{ $t('select') }}
            </option>
            <option value="BR">
              {{ $t('brazilian') }}
            </option>
            <option value="Outra">
              {{ $t('foreigner') }}
            </option>
          </select>
        </div>
        <div
          class="form__row__column"
          :class="{
            error: !isValid('cpf', true),
            success: isValid('cpf'),
          }"
        >
          <label for="cpf">
            {{ $t('cpf') }}
          </label>
          <input
            id="cpf"
            ref="cpf"
            v-model.trim="guest.cpf"
            v-imask="cpf.mask"
            type="text"
            :placeholder="cpf.placeholder"
            maxlength="14"
            @blur="startValidate('cpf')"
          />
        </div>
      </div>
      <h3 class="title title--smaller">
        {{ $t('contact') }}
      </h3>
      <div class="form__row">
        <div
          class="form__row__column"
          :class="{
            error: !isValid('email', true),
            success: isValid('email'),
          }"
        >
          <label for="email">
            {{ $t('email') }}
            <template v-if="isChild">
              {{ $t('optionalField') }}
            </template>
          </label>
          <input
            id="email"
            v-model.trim="guest.email"
            type="email"
            placeholder="Ex. nome@email.com"
            @blur="startValidate('email')"
          />
        </div>
      </div>
      <div class="form__row">
        <div
          class="form__row__column"
          :class="{
            error: !isValid('phone', true),
            success: isValid('phone'),
          }"
        >
          <label for="phone">
            {{ $t('phone') }}
            <template v-if="isChild">
              {{ $t('optionalField') }}
            </template>
          </label>
          <input
            id="phone"
            ref="phone"
            v-model.trim="guest.phone"
            v-imask="phone.mask"
            type="tel"
            :placeholder="phone.placeholder"
            :maxlength="phone.length"
            @blur="startValidate('phone')"
          />
        </div>
      </div>

      <h3 class="title title--smaller">
        {{ $t('address') }}
      </h3>
      <div
        v-if="!guest.isMainGuest"
        class="form__use-mainguest-address"
      >
        <label>
          <Checkbox
            id="useMainGuestAddress"
            v-model="guest.useMainGuestAddress"
            color="var(--primary-color)"
          />
          <span>{{ $t('useMainGuestAddress') }}</span>
        </label>
      </div>
      <div v-if="!guest.useMainGuestAddress">
        <div class="form__row">
          <div
            class="form__row__column"
            :class="{
              error: !isValid('countryCode', true),
              success: isValid('countryCode'),
            }"
          >
            <label for="country">
              {{ $t('country') }}
            </label>
            <select
              id="country"
              v-model="guest.countryCode"
              @change="startValidate('countryCode')"
            >
              <option
                v-for="country in countries"
                :key="country.pt"
                :value="country.code"
              >
                {{ country[language] }}
              </option>
            </select>
          </div>
          <div
            class="form__row__column half"
            :class="{
              error: !isValid('cep', true),
              success: isValid('cep'),
            }"
          >
            <label for="cep">
              {{ $t('cep') }}
            </label>
            <input
              id="cep"
              ref="cep"
              v-model.trim="guest.cep"
              v-imask="cep.mask"
              type="text"
              :placeholder="cep.placeholder"
              maxlength="9"
              @blur="startValidate('cep')"
            />
          </div>
        </div>
        <div class="form__row">
          <div
            class="form__row__column"
            :class="{
              error: !isValid('address', true),
              success: isValid('address'),
            }"
          >
            <label for="address">
              {{ $t('address') }}
            </label>
            <input
              id="address"
              ref="address"
              v-model.trim="guest.address"
              type="text"
              placeholder="Ex. Rua Salgueiro, Auxiliadora"
              maxlength="60"
              @blur="startValidate('address')"
            />
          </div>
          <div
            class="form__row__column quarter"
            :class="{
              error: !isValid('addressNumber', true),
              success: isValid('addressNumber'),
            }"
          >
            <label for="addressNumber">
              {{ $t('addressNumber') }}
            </label>
            <input
              id="addressNumber"
              ref="addressNumber"
              v-model.trim="guest.addressNumber"
              type="number"
              placeholder="Ex. 456"
              maxlength="60"
              @blur="startValidate('addressNumber')"
            />
          </div>
          <div class="form__row__column quarter">
            <label for="addressComplement">
              {{ $t('addressComplement') }}
              {{ $t('optionalField') }}
            </label>
            <input
              id="addressComplement"
              v-model.trim="guest.addressComplement"
              type="text"
              placeholder="Ex. Apto. 301"
              maxlength="60"
            />
          </div>
        </div>
        <div class="form__row">
          <div
            class="form__row__column"
            :class="{
              error: !isValid('state', true),
              success: isValid('state'),
            }"
          >
            <label for="state">
              {{ $t('state') }}
            </label>
            <select
              v-if="guest.countryCode === 'BR'"
              id="state"
              v-model="guest.stateCode"
              @change="startValidate('state')"
            >
              <option value="">
                {{ $t('select') }}
              </option>
              <option
                v-for="state in states"
                :key="state.code"
                :value="state.code"
              >
                {{ state.name }}
              </option>
            </select>
            <input
              v-else
              id="state"
              v-model="guest.stateName"
              type="text"
              placeholder="Ex. São Paulo"
              maxlength="60"
              @blur="startValidate('state')"
            />
          </div>
        </div>
        <div class="form__row">
          <div
            class="form__row__column"
            :class="{
              error: !isValid('cityName', true),
              success: isValid('cityName'),
            }"
          >
            <label for="city">
              {{ $t('city') }}
            </label>
            <input
              id="city"
              v-model.trim="guest.cityName"
              type="text"
              placeholder="Ex. Porto Alegre"
              maxlength="60"
              @blur="startValidate('cityName')"
            />
          </div>
        </div>
      </div>
      <div
        v-if="consentText"
        class="form__optin"
      >
        <label>
          <Checkbox
            id="optin"
            v-model="guest.contactOptin"
            color="var(--primary-color)"
          />
          <span v-html="consentText"></span>
        </label>
      </div>
    </template>
    <div class="form__actions">
      <button
        v-if="!isFirstView"
        class="cta vazado"
        type="button"
        @click="cancel"
      >
        {{ $t('cancel') }}
      </button>
      <button
        type="submit"
        class="cta"
      >
        {{ isFirstView && hasIntegration ? $t('identify') : $t('save') }}
      </button>
    </div>
  </form>
</template>

<script>
import { mapGetters } from 'vuex';
import Vue from 'vue';
import isValidEmail from '@/validation/validators/email';
import isValidCpf from '@/validation/validators/cpf';
import isValidDate from '@/validation/validators/date';
import countries from '@/helpers/countries.json';
import states from '@/helpers/brazilianStates.json';
import professions from '@/helpers/professions.json';
import { IMaskDirective } from 'vue-imask';
import flatpickr from 'flatpickr';
import { Portuguese } from 'flatpickr/dist/l10n/pt';
import 'flatpickr/dist/flatpickr.css';
import Checkbox from '@/components/site/Checkbox.vue';

export default {
  name: 'SignInForm',
  components: {
    Checkbox,
  },
  directives: {
    imask: IMaskDirective,
  },
  props: {
    activeGuest: {
      type: Object,
      default: () => ({}),
    },
    activeGuestIndex: {
      type: Number,
      default: undefined,
    },
    mainGuest: {
      type: Object,
      default: () => ({}),
    },
    isFirstView: {
      type: Boolean,
      default: true,
    },
    hasIntegration: {
      type: Boolean,
      default: false,
    },
    hasPmsIntegration: {
      type: Boolean,
      default: false,
    },
    consentText: {
      type: String,
      default: '',
    },
    settings: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      states,
      countries,
      professions,
      isDirty: [],
      guest: {},
      dateMask: {
        mask: '00/00/0000',
      },
      searchCep: true,
    };
  },
  computed: {
    requiredFields() {
      const fields = ['name', 'surname'];

      if (
        this.isFirstView ||
        (!this.hasIntegration && this.activeGuest === this.mainGuest)
      ) {
        fields.push('reservationCode', 'checkInDate');
      }

      if (!this.isFirstView || !this.hasIntegration) {
        fields.push('birthDate', 'cpf', 'nationality');

        if (!this.guest.useMainGuestAddress) {
          fields.push(
            'countryCode',
            'cep',
            'address',
            'addressNumber',
            'state',
            'cityName',
          );
        }

        if (!this.isChild) {
          fields.push('email', 'phone', 'profession');
        }
      }

      return fields;
    },
    isChild() {
      return (
        !!this.guest.birthDate &&
        this.ageBelowOf(this.guest.birthDate, 12) &&
        !this.guest.isMainGuest
      );
    },
    isFormValid() {
      return this.requiredFields.every((field) => this.validate(field));
    },
    isBrazilian() {
      return this.guest.nationality === 'BR';
    },
    isAddressBrazilian() {
      return this.guest.countryCode === 'BR';
    },
    isLetsbookOrigin() {
      return this.settings.origin === 'Letsbook';
    },
    phone() {
      return this.isLetsbookOrigin
        ? { placeholder: '+00 (00) 000000000', mask: null, length: 21 }
        : {
            placeholder: '(00) 99999-9999',
            mask: { mask: '(00) 00000-0000' },
            length: 15,
          };
    },
    cpf() {
      return this.isBrazilian
        ? { placeholder: '000.000.000-00', mask: { mask: '000.000.000-00' } }
        : { placeholder: '00000000000', mask: null };
    },
    cep() {
      return this.isAddressBrazilian
        ? { placeholder: '00000-000', mask: { mask: '00000-000' } }
        : { placeholder: '00000000', mask: null };
    },
    language() {
      return this.activePage.language;
    },
    ...mapGetters(['activePage']),
  },
  watch: {
    activeGuest: {
      handler(guest) {
        this.guest = guest;
      },
      immediate: true,
    },
    // eslint-disable-next-line func-names
    'guest.cep': function (value) {
      this.$nextTick(() => {
        if (this.isValid('cep') && this.isAddressBrazilian && this.searchCep) {
          const cep = value.replace('-', '');
          fetch(`https://viacep.com.br/ws/${cep}/json/`)
            .then((res) => res.json())
            .then((data) => {
              if (data && data.localidade) {
                this.fillAddressFromCEP(data);
              } else {
                this.$refs.address.focus();
              }
            })
            .catch(() => {
              this.$refs.address.focus();
            });
        }
        if (!this.searchCep) {
          this.searchCep = true;
        }
      });
    },
    // eslint-disable-next-line func-names
    'guest.checkInDate': function (value) {
      this.$nextTick(() => {
        const { value: maskValue } = this.$refs.checkInDate.maskRef.masked;
        if (value !== maskValue) {
          this.$refs.checkInDate.maskRef.updateValue();
          this.guest.checkInDate = maskValue;
        }
      });
    },
    // eslint-disable-next-line func-names
    'guest.birthDate': {
      handler(value) {
        if (value) {
          this.$nextTick(() => {
            const { value: maskValue } = this.$refs.birthDate.maskRef.masked;
            if (value !== maskValue) {
              this.$refs.birthDate.maskRef.updateValue();
              this.guest.birthDate = maskValue;
            }
          });
        }
      },
      immediate: true,
    },
  },
  mounted() {
    const flatpickrOptions = {
      dateFormat: 'd/m/Y',
      allowInput: true,
      locale: {
        ...Portuguese,
      },
    };
    if (this.$refs.checkInDate) {
      flatpickr(this.$refs.checkInDate, {
        ...flatpickrOptions,
        minDate: 'today',
      });
    }
    if (this.$refs.birthDate) {
      flatpickr(this.$refs.birthDate, {
        ...flatpickrOptions,
        maxDate: 'today',
      });
    }
  },
  methods: {
    validate(field) {
      if (field === 'state') {
        return this.isAddressBrazilian
          ? this.guest.stateCode
          : this.guest.stateName;
      }
      if (!this.guest[field]) {
        return false;
      }
      switch (field) {
        case 'email':
          return isValidEmail(this.guest[field]);
        case 'cpf':
          return !this.isBrazilian || isValidCpf(this.guest[field]);
        case 'checkInDate':
        case 'birthDate':
          return isValidDate(this.guest[field]);
        case 'phone':
          return this.guest[field].replace(/\D/g, '').length >= 10;
        case 'cep':
          return (
            !this.isAddressBrazilian ||
            /^[0-9]{5}-[0-9]{3}$/.test(this.guest[field])
          );
        default:
          return !!this.guest[field];
      }
    },
    isValid(field, filterDirty) {
      if (filterDirty) {
        return !this.isDirty.includes(field) || this.validate(field);
      }
      return this.validate(field);
    },
    startValidate(field) {
      if (!this.isDirty.includes(field)) {
        this.isDirty.push(field);
      }
      if (
        this.isDirty.includes(field) &&
        !this.requiredFields.includes(field)
      ) {
        const fieldIndex = this.isDirty.indexOf(field);
        if (fieldIndex !== -1) {
          this.isDirty.splice(fieldIndex, 1);
        }
      }
    },
    ageBelowOf(birthDate, value) {
      const date = birthDate.split('/');
      const birthDateTimestamp = new Date(
        `${date[1]}/${date[0]}/${Number(date[2]) + value}`,
      ).getTime();
      return birthDateTimestamp > new Date().getTime();
    },
    fillAddressFromCEP(address) {
      Vue.set(this.guest, 'cityName', address.localidade);
      Vue.set(this.guest, 'cityCode', address.ibge || '');
      Vue.set(this.guest, 'stateCode', address.uf || '');
      if (!this.guest.countryCode) {
        Vue.set(this.guest, 'countryCode', 'BR');
      }
      if (address.logradouro) {
        Vue.set(
          this.guest,
          'address',
          address.logradouro.concat(
            address.bairro ? `, ${address.bairro}` : '',
          ),
        );
        this.$refs.addressNumber.focus();
      }
    },
    save() {
      if (this.isFormValid) {
        if (!this.consentText) {
          delete this.guest.contactOptin;
        }
        this.guest.consentText = this.consentText;

        if (!this.guest.useMainGuestAddress) {
          const countryObject = this.countries.find(
            (country) => country.code === this.guest.countryCode,
          );
          Vue.set(this.guest, 'countryName', countryObject.pt);

          if (this.isAddressBrazilian && this.guest.stateCode) {
            const stateObject = this.states.find(
              (state) => state.code === this.guest.stateCode,
            );
            Vue.set(this.guest, 'stateName', stateObject.name);
          } else {
            delete this.guest.stateCode;
          }
        }

        this.$emit('save', this.guest);
      } else {
        this.isDirty = this.requiredFields;
        this.$nextTick(() => {
          const { offsetTop } = this.$refs.form.querySelector('.error');
          const scroll = document.querySelector('.preview > .scroll-area');
          (scroll || window).scrollTo({
            top: offsetTop - 25,
            behavior: 'smooth',
          });
        });
      }
    },
    cancel() {
      this.$emit('cancel');
    },
  },
  i18n: {
    messages: {
      pt: {
        guest: 'Hóspede',
        mainGuest: 'Hóspede principal',
        main: ' (principal)',
        asideGuest: 'Acompanhante',
        companion: ' (acompanhante)',
        reservation: 'Reserva',
        reservationCode: 'Número de reserva',
        checkInDate: 'Data de check-in',
        host: 'Hóspede',
        name: 'Nome',
        isChild: 'Estou cadastrando uma criança?',
        surname: 'Sobrenome',
        contact: 'Contato',
        email: 'E-mail',
        phone: 'Telefone celular',
        profile: 'Perfil',
        birthDate: 'Data de nascimento',
        profession: 'Profissão',
        nationality: 'Nacionalidade',
        cpf: 'Número do documento',
        address: 'Endereço',
        useMainGuestAddress: 'Usar endereço do hóspede principal',
        cep: 'CEP',
        addressNumber: 'Número',
        addressComplement: 'Complemento',
        country: 'País',
        state: 'Estado',
        city: 'Cidade',
        cancel: 'Voltar',
        save: 'Salvar hóspede',
        identify: 'Identificar reserva',
        select: 'Selecione',
        other: 'Outro',
        brazilian: 'Brasileiro | CPF',
        foreigner: 'Estrangeiro | Passaporte',
        optionalField: '(opcional)',
      },
      en: {
        guest: 'Guest',
        mainGuest: 'Main Guest',
        main: ' (main)',
        asideGuest: 'Additional guest',
        companion: ' (companion)',
        reservation: 'Reservation',
        reservationCode: 'Reservation Number',
        checkInDate: 'Check-in Date',
        host: 'Guest',
        name: 'Name',
        isChild: 'Am I registering a child?',
        surname: 'Last Name',
        contact: 'Contact',
        email: 'E-mail',
        phone: 'Phone number',
        profile: 'Profile',
        birthDate: 'Date of birth',
        profession: 'Profession',
        nationality: 'Nationality',
        cpf: 'ID Number',
        address: 'Address',
        useMainGuestAddress: 'Use main guest address',
        cep: 'Postal Code',
        addressNumber: 'Number',
        addressComplement: 'Complement',
        country: 'Country',
        state: 'State',
        city: 'City',
        cancel: 'Back',
        save: 'Save guest',
        identify: 'Identify reservation',
        select: 'Select',
        other: 'Other',
        brazilian: 'Brazilian | CPF',
        foreigner: 'Foreign | Passport',
        optionalField: '(optional)',
      },
      es: {
        guest: 'Huésped',
        mainGuest: 'Huésped Principal',
        main: ' (principal)',
        asideGuest: 'Acompañante',
        companion: ' (acompañante)',
        identification: 'Identificación',
        reservation: 'Reserva',
        reservationCode: 'Número de la reserva',
        checkInDate: 'Fecha de check-in',
        host: 'Huésped',
        name: 'Nombre',
        isChild: '¿Estoy registrando a un niño?',
        surname: 'Apellido',
        contact: 'Contacto',
        email: 'E-mail',
        phone: 'Teléfono',
        profile: 'Perfil',
        birthDate: 'Fecha de nacimiento',
        profession: 'Profesión',
        nationality: 'Nacionalidad',
        cpf: 'Número de documento',
        address: 'Dirección',
        useMainGuestAddress: 'Usar la dirección del huésped principal',
        cep: 'Código Postal',
        addressNumber: 'Número',
        addressComplement: 'Complemento',
        country: 'País',
        state: 'Estado',
        city: 'Ciudad',
        cancel: 'Volver',
        save: 'Guardar Huésped',
        identify: 'Identificar reserva',
        select: 'Selecciona',
        other: 'Otro',
        brazilian: 'Brasileño | CPF',
        foreigner: 'Extranjero | Pasaporte',
        optionalField: '(opcional)',
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.form {
  max-width: 966px;

  h2 {
    margin-bottom: 40px;
  }

  h3 {
    margin: 20px 0 30px;

    button {
      cursor: pointer;
      color: var(--primaryColor);
      text-decoration: underline;
      border: 0;
      padding: 0;
      background: none;
    }
  }

  &__title {
    margin-bottom: 30px;
    display: flex;
    align-items: center;

    > h3 {
      margin: 0;
      margin-right: 6px;
    }
  }

  &__row {
    margin-bottom: 30px;

    @media (min-width: 640px) {
      display: flex;
      gap: 10px;
    }

    &__column {
      position: relative;
      flex: 1 1 50%;

      & + & {
        @media (max-width: 639.8px) {
          margin-top: 30px;
        }
      }

      &.half {
        flex-grow: 0;
      }

      &.quarter {
        flex-basis: 25%;
      }

      &.error {
        label,
        input,
        input::placeholder,
        select {
          color: $lb-red;
          font-weight: $lb-semibold;
          border-color: $lb-red;
        }

        input::placeholder {
          color: rgba($lb-red, 0.6);
        }
      }

      &.success {
        input,
        select {
          border-color: $success;
        }

        &::after {
          content: '';
          position: absolute;
          right: 20px;
          top: 50%;
          transform: translateY(-50%);
          width: 13px;
          height: 10px;
          background-image: url('../../assets/fieldSuccess.svg');
          background-size: 100% 100%;
        }
      }

      label {
        position: absolute;
        left: 10px;
        top: -12px;
        font-size: $size-normal;
        padding: 0 5px;
        background-color: $lb-white;
        z-index: 1;

        @include media-breakpoint-between(sm, lg) {
          font-size: $size-smaller;
          top: -6px;
        }
      }

      input[type='number'] {
        -moz-appearance: textfield;

        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
      }

      input {
        &:-webkit-autofill,
        &:-webkit-autofill:hover,
        &:-webkit-autofill:focus,
        &:-webkit-autofill:active {
          box-shadow: 0 0 0 30px $lb-white inset !important;
          -webkit-box-shadow: 0 0 0 30px $lb-white inset !important;
        }
      }

      input,
      select {
        border: 1px solid $lb-black;
        background-color: $lb-white;
        border-radius: 5px;
        padding: 14px 12px;
        outline: none;
        height: 50px;
        width: 100%;
        font-size: $size-normal;

        &:focus {
          outline: 1px dotted;
          outline: 5px auto -webkit-focus-ring-color;
        }

        option {
          color: $lb-black;
        }
      }
    }
  }

  &__optin,
  &__use-mainguest-address {
    display: flex;

    label {
      display: flex;
      align-items: center;

      .checkbox {
        flex-shrink: 0;
      }

      span {
        line-height: 1.3;
        margin-left: 8px;
      }
    }
  }

  &__use-mainguest-address {
    margin-bottom: 30px;
  }

  &__actions {
    margin-top: 60px;
    justify-content: right;

    @include media-breakpoint-up(sm) {
      display: flex;
    }

    .cta:not(.vazado) {
      margin-left: 15px;
    }

    .cta + .cta {
      margin-top: 35px;

      @include media-breakpoint-up(sm) {
        margin-top: 0px;
      }
    }
  }
}
</style>

<style lang="scss">
// For aplly css in flatpickr input when mobile
.form {
  &__row {
    &__column {
      &.error {
        .flatpickr-input {
          color: $lb-red;
          font-weight: $lb-semibold;
          border-color: $lb-red;
        }

        .flatpickr-input::placeholder {
          color: rgba($lb-red, 0.6);
        }
      }

      &.success {
        .flatpickr-input {
          border-color: $success;
        }
      }

      .flatpickr-input {
        border: 1px solid $lb-black;
        font-family: $lb-content-font;
        background-color: $lb-white;
        border-radius: 5px;
        padding: 14px 12px;
        outline: none;
        height: 50px;
        width: 100%;
        font-size: $size-normal;

        &:focus {
          outline: 1px dotted;
          outline: 5px auto -webkit-focus-ring-color;
        }
      }
    }
  }
}
</style>
